/**
 * Created by fers on 03.08.16.
 */
/* eslint-disable react/no-multi-comp */

import DragSource from 'react-dnd/lib/DragSource';
import DropTarget from 'react-dnd/lib/DropTarget';
import React from 'react';
import {Button, ButtonGroup} from 'react-bootstrap';
import PropTypes from 'prop-types';
import ScreenDisplayer from './../box-utils/ScreenDisplayer.react.js';
import LineDisplayer from './../box-utils/LineDisplayer.react.js';
import { findDOMNode } from 'react-dom';
import Immutable from 'immutable';
import LoadingButton from '../../fw/LoadingButton.react';
import {injectIntl, defineMessages, IntlShape} from 'react-intl';

import {convertViewForStore} from './convertFromBoxesToView';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import {displayError} from '../../app/errorDisplayer';
import URLMapping from '../../app/URLMapping';
import WidgetConfigOpeners from './WidgetConfigOpeners';
import {getDataSourceName} from './DataSourceList.react';

import './ScreenSettings.scss';

const messages = defineMessages({
    addButtonTitle: {defaultMessage: "Add this component to your panel", id:"DraggableBoxRaw.addButtonTitle"},
	editComponent: {defaultMessage: "Customize component view", id:"DraggableBoxRaw.editComponent"},
	removeButtonTitle: {defaultMessage: "Remove component from panel", id:"DraggableBoxRaw.removeButtonTitle"},
});

/**
 * Implements the drag source contract.
 */
export const screenSource = {
    beginDrag(props) {
        let view = props.view;

        if (!view) {
            let dataSource = props.dataSource;

            view = convertViewForStore(props.screen, dataSource, props.viewTemplate);

            /* add view to the screen views to allow rendering */
            props.screenSettings.addDraggingView(view);
        } else {
            /* we need to add small delay so HTML5 dnd backend can take screenshot of the <div/> before we hide it */
            setTimeout(() => {
                props.screenSettings.setDragging(view);
            }, 10);
        }

        let result = {
            view_id: view['realId'],
            view: view,
            content: props.screenSettings.state.content,
            screenSettings: props.screenSettings,
        };

        return result;
    },

    endDrag(props, monitor, component) {
        let draggingId = props.screenSettings.state.dragging;

        props.screenSettings.endDragging();

        if (!monitor.didDrop()) {
            /* user cancelled the drop, so revert changes */
            props.screenSettings.setState({content: monitor.getItem().content});

            return;
        }

        // When dropped on a compatible target, do something
        const item = monitor.getItem();
        const dropResult = monitor.getDropResult();

        let screen = props.screenSettings.getScreen();
        let content = props.screenSettings.state.content;

        if (dropResult.type && dropResult.type === "trash") {
            props.screenSettings.trashView(draggingId);
        } else if (!(dropResult.type && dropResult.type === "ToAddBox")) {

			let dataSource = item.view.dataSource;
			if (!dataSource) {
				dataSource = screen.dataSources.find((dataSource) => dataSource['realId'] === item.view.dataSource_id);
			}

            let save = convertViewForStore(screen, dataSource, item.view);

            if (props.view) {
                props.screenSettings.pushContent(content, item.content);
            } else {
                /* create view */
                props.screenSettings.createView(save).then((savedValue) => {

                    /* we can't set view Id for saving, so we replace it with new ID */
                    const dragBox = recursiveSearch(content, [], item.view.realId);

                    if (dragBox) {
                        content = content.updateIn([...dragBox.keyPath, 'views'], (update) => {
                            return update.set(dragBox.idx, savedValue['createView']['edge']['node']['realId']);
                        });
                    }

                    /* hack to replace currently loaded item - to speed refresh */
                    item.view.realId = savedValue['createView']['edge']['node']['realId'];

                    props.screenSettings.pushContent(content, item.content);
                }).catch((e) => {
                    displayError(e);
                });
            }
        }
    }
};

/**
 * Recursively search content for viewId.
 * @param content
 * @param viewId
 * @returns {*}
 */
export function recursiveSearch(content, keyPath, viewId) {
    if (content.has('views')) {
        let idx = content.get('views').findIndex((searchId) => {
            return (searchId === viewId);
        });

        if (idx !== -1) {
            return {
                list: content,
                idx: idx,
                keyPath: keyPath,
                view: content.get('views').get(idx)
            };
        }
    }

    if (content.has('children')) {
        let list = content.get('children');
        for (let i = 0, len = list.size; i < len; i++) {
            let child = list.get(i);
            let res = recursiveSearch(child, [...keyPath, 'children', i], viewId);
            if (res) {
                return res;
            }
        }
    }

    return;
}

const screenTarget = {
    hover(props, monitor, component) {
        let draggedView = monitor.getItem().view;
        const dragId = draggedView['realId'];

        let content = props.screenSettings.state.content;

        const dragBox = recursiveSearch(content, [], dragId);

        /* if we are not moving an existing box */
        if (!props.view) {

            if (dragBox) {
                /* remove dragged item if it is present somewhere (so we can add it somewhere later) */
                content = content.deleteIn([...dragBox.keyPath, 'views', dragBox.idx]);
                props.screenSettings.setState({content: content});
            }

            return;
        }
        const hoverId = props.view['realId'];

        if (dragId === hoverId) {
            return;
        }

        const hoverBox = recursiveSearch(content, [], hoverId);

        if (!hoverBox) {
            return;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();

        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        let idx;
        // Dragging downwards
        if (hoverClientY < hoverMiddleY) {
            idx = hoverBox.idx;
            // Dragging upwards
        } else if (hoverClientY > hoverMiddleY) {
            idx = hoverBox.idx + 1;
        }

        /* remove dragged item if it is present somewhere (so we can add it somewhere else later) */
        if (dragBox) {
            /* if there is nothing to change, return */
            if (idx === dragBox.idx && dragBox.keyPath.join(', ') === hoverBox.keyPath.join(', ')) {
                return;
            }

            /* remove dragged item if it is present somewhere */
            content = content.deleteIn([...dragBox.keyPath, 'views', dragBox.idx]);
        }

        content = content.updateIn([...hoverBox.keyPath, 'views'], (list) => {
            return list.insert(idx, dragId);
        });

        props.screenSettings.setState({content: content});
    }
};

const getScreenTarget = function(type) {
	return {
		hover: screenTarget.hover,
		drop: (props, monitor, component) => {
			return {
				type: type
			};
		}
	};
};

const appendTarget = {
    hover(props, monitor, component) {
        let draggedView = monitor.getItem().view;
        const dragId = draggedView['realId'];

        let content = props.screenSettings.state.content;

        const dragBox = recursiveSearch(content, [], dragId);

        if (dragBox) {
            /* if there is nothing to change, return */
            if (dragBox.keyPath.join(', ') === props.keyPath.join(', ')) {
                return;
            }

            /* remove dragged item if it is present somewhere */
            content = content.deleteIn([...dragBox.keyPath, 'views', dragBox.idx]);
        }

        content = content.updateIn([...props.keyPath, 'views'], (list) => {
            if (!list) {
                list = new Immutable.List();
            }
            return list.push(dragId);
        });

        props.screenSettings.setState({content: content});

    },

    drop(props, monitor, component) {
        return {
            type: "append",
            keyPath: props.keyPath,
        };
    }
};

        /**
 * Specifies which props to inject into your component.
 */
function collect(connect, monitor) {
    return {
        // Call this function inside render()
        // to let React DnD handle the drag events:
        connectDragSource: connect.dragSource(),
        // You can ask the monitor about the current drag state:
        isDragging: monitor.isDragging()
    };
}

export const ItemTypes = {
    BOX: 'box',
    BOX_TO_REMOVE: 'box-to-remove',
};

class DraggableBoxRaw extends React.Component {
    static propTypes = {
        children: PropTypes.any,
        connectDragSource: PropTypes.func.isRequired,
        connectDropTarget: PropTypes.func.isRequired,
        dataSource: PropTypes.object,
        dataSource_id: PropTypes.string,
        intl: PropTypes.object,
        isDragging: PropTypes.bool.isRequired,
        screen: PropTypes.object,
        screenSettings: PropTypes.object,
        screen_id: PropTypes.string,
        view: PropTypes.object,
        viewTemplate: PropTypes.object,
    };

	static contextTypes = {
        router: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.state = {
            trashHover: false,
			configHover: false,
			adding: false
        };
    }

	onTrashHoverStart = () => {
		this.setState({
			trashHover: true
		});
	}

	onTrashHoverEnd = () => {
		this.setState({
			trashHover: false
		});
	}

	onConfigHoverStart = () => {
		this.setState({
			configHover: true
		});
	}

	onConfigHoverEnd = () => {
		this.setState({
			configHover: false
		});
	}

	onTrashClick = () => {
		return this.props.screenSettings.trashView(this.props.view['realId']);
	}

	onAddClick = () => {
		this.setState({
			adding: true
		});
		return this.props.screenSettings.insertView(this.props.viewTemplate).finally(() => {
			this.setState({
				adding: false
			});
		});
	}

	openConfig() {
		let pathObject = {
			organisation_id: this.context.router.params.organisationId,
			channel_id: this.context.router.params.channelId,
			screen_id: this.props.screen.realId,
			viewId: this.props.view.realId,
			opener: WidgetConfigOpeners.SCREEN_SETTINGS,
		};

		let path = URLMapping.getUrl('panel', 'widget-config', pathObject);
		this.context.router.push(path);
	}

    render() {
        const { connectDragSource, connectDropTarget, isDragging } = this.props;
		let visible = true;

        if (this.props.view) {
            /* isDragging doesn't work properly when we remove dragged DIV (and move it somewhere else)
             * - so we use dragged ID to find dragged view
             */
            if (this.props.view && this.props.screenSettings.state.dragging === this.props.view['realId']) {
				visible = false;
            }
        } else if (isDragging) {
			visible = false;
        }

        let inside = (
            <div className={"draggableBox" + (visible ? '' : ' notVisible ') + (this.state.hover ? ' hover ' : '') + (this.state.trashHover ? ' trashHover ' : '')  + (this.state.configHover ? ' configHover ' : '')}>
                {this.props.view ? <div className="draggableBoxRemove">
					<ButtonGroup>
						{('json-table' === this.props.view.handler || 'json-detail' === this.props.view.handler)
							? <Button
								bsStyle="info"
								onClick={this.openConfig.bind(this)}
								onMouseOut={this.onConfigHoverEnd}
								onMouseOver={this.onConfigHoverStart}
								title={this.props.intl.formatMessage(messages.editComponent)}
								><span className="fa fa-pencil"></span></Button>
							: null
						}
						<LoadingButton
							className="btn btn-danger"
							onClick={this.onTrashClick}
							onMouseOut={this.onTrashHoverEnd}
							onMouseOver={this.onTrashHoverStart}
							title={this.props.intl.formatMessage(messages.removeButtonTitle)}
						>
							<span className="fa fa-trash-o"></span>
						</LoadingButton>
					</ButtonGroup>
                </div> : null}
                <div className="panel-slide">
                </div>
                {this.props.children}
            </div>
        );

        if (this.props.view) {
            return connectDragSource(connectDropTarget(
                inside
            ));
        } else {
            return (
                <div className={"draggableBoxWrapper" + (this.state.adding ? ' adding' : '')}>
                    {this.props.isDragging ? null : <div className="draggableBoxAdd">
                        <LoadingButton className="btn btn-success"
										onClick={this.onAddClick}
										title={this.props.intl.formatMessage(messages.addButtonTitle)}><span
                            className="fa fa-plus"></span></LoadingButton>
                    </div>}

                    {connectDragSource(connectDropTarget(inside))}
                </div>
            );
        }
    }
}

export class ExistingScreenDisplayer extends ScreenDisplayer {
    static propTypes = {
        canModify: PropTypes.bool,
		intl: PropTypes.object.isRequired,
        screen: PropTypes.object.isRequired,
        screenSettings: PropTypes.object.isRequired
    };

    createElement(viewWithData, key) {
        let view = viewWithData.view;
		let idx = this.props.screen.dataSources.findIndex((dataSource) => dataSource['realId'] === view.dataSource_id);
        let dataSource = this.props.screen.dataSources.find((dataSource) => dataSource['realId'] === view.dataSource_id);

		let {description} = getDataSourceName(this.props.intl, this.props.screen.dataSources, dataSource);

        return (
            <div key={key} style={{position: 'relative'}} title={dataSource && dataSource.name ? dataSource.name : (dataSource && dataSource.entityDef ? dataSource.entityDef.name : null)}>
                {this.props.canModify ?
                    <ToMoveBox
                        screen={this.props.screen}
                        screenSettings={this.props.screenSettings}
                        view={view}
                        view_id={view.realId}
                    >
                        <div className="dataSource-title" title={description}><span className="dataSource-idx">#{dataSource.idx || idx + 1}</span></div>
                        {super.createElement(viewWithData, 0)}
                    </ToMoveBox>
                :
                    <div className={"draggableBoxWrapper"}>
                        <div className="dataSource-title" title={description}><span className="dataSource-idx">#{dataSource.idx || idx + 1}</span></div>
                        {super.createElement(viewWithData, 0)}
                    </div>}
            </div>
        );
    }
}

export class AllowedScreenDisplayer extends LineDisplayer {
    static propTypes = {
        canModify: PropTypes.bool,
        isPreview: PropTypes.bool,
        screen: PropTypes.object.isRequired,
        screenSettings: PropTypes.object.isRequired,
    };

    createElement(viewWithData) {
        let view = viewWithData.view;

        let title = "";
        if (this.props.isPreview) {
            if (view.entityViewDef) {
                if (view.entityViewDef.title) {
                    title = view.entityViewDef.title;
                } else {
                    title = view.entityViewDef.name;
                }
            } else if (view.name) {
                title = view.name;
            }
        }

        return (
            <div>
                {this.props.isPreview ? <h4>{title}</h4> : null}

                <div>
                    {this.props.canModify ?
                        <ToAddBox
                        dataSource={view.dataSource}
                        dataSource_id={view.dataSource ? view.dataSource.realId : null}
                        screen={this.props.screen}
                        screenSettings={this.props.screenSettings}
                        viewTemplate={view}
                        >
                            {super.createElement(viewWithData, 0)}
                        </ToAddBox>
                    :
                        <div className={"draggableBoxWrapper"}>
                            <div className={"draggableBox"}>
                                <div className="panel-slide">
                                </div>
                                {super.createElement(viewWithData, 0)}
                            </div>
                        </div>
                    }
                </div>
            </div>
        );
    }
}

class DragAppendRaw extends React.Component {
    static propTypes = {
        connectDropTarget: PropTypes.func.isRequired,
        isDragging: PropTypes.bool.isRequired,
        keyPath: PropTypes.array.isRequired,
        screenSettings: PropTypes.object.isRequired,
    };

    constructor(props) {
        super(props);
        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    render() {
        const { isDragging, connectDropTarget } = this.props;

        return (
            connectDropTarget(
                <div className={"panelLayoutContentAppend " + (isDragging ? "dragAppendActive" : "")}>
                </div>
            )
        );
    }
}

// Export the wrapped component:
export const ToAddBox =
    DropTarget(ItemTypes.BOX, getScreenTarget("ToAddBox"), connect => ({
        connectDropTarget: connect.dropTarget()
    }))(DragSource(ItemTypes.BOX, screenSource, collect)(injectIntl(DraggableBoxRaw)));

export const ToMoveBox =
    DropTarget([ItemTypes.BOX, ItemTypes.BOX_TO_REMOVE], getScreenTarget("ToMoveBox"), connect => ({
        connectDropTarget: connect.dropTarget()
    }))(DragSource(ItemTypes.BOX_TO_REMOVE, screenSource, collect)(injectIntl(DraggableBoxRaw)));

export const DragAppend = DropTarget([ItemTypes.BOX, ItemTypes.BOX_TO_REMOVE], appendTarget, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
}))(DragAppendRaw);
